import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  CheckCircle,
  Calendar,
  Upload,
  FileText,
  ChevronRight,
  Clock,
  AlertCircle,
  TrendingUp,
  Eye,
  MessageSquare,
  Sparkles,
  Camera,
  Video,
  Globe,
  Palette,
  BookOpen,
  DollarSign,
  Send,
  CheckCircle2,
  XCircle,
  Plus,
  ArrowRight
} from 'lucide-react';
import { Progress } from '../components/ui/progress';

const DemoPortalPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');

  // Demo data reflecting the new Service Request workflow
  const demoStats = {
    totalRequests: 3,
    pendingQuotes: 1,
    activeProjects: 2,
    upcomingBookings: 1,
  };

  const demoServiceRequests = [
    {
      id: 'REQ-2026-001',
      title: 'Corporate Brand Video',
      service: 'Videography',
      status: 'in_progress',
      statusLabel: 'In Progress',
      statusColor: 'bg-blue-500',
      budget: '₹1,50,000',
      date: '2026-02-15',
    },
    {
      id: 'REQ-2026-002',
      title: 'Product Photography Session',
      service: 'Photography',
      status: 'quoted',
      statusLabel: 'Quote Sent',
      statusColor: 'bg-yellow-500',
      budget: '₹45,000',
      date: '2026-02-20',
    },
    {
      id: 'REQ-2026-003',
      title: 'Social Media Campaign',
      service: 'Digital Marketing',
      status: 'approved',
      statusLabel: 'Approved',
      statusColor: 'bg-green-500',
      budget: '₹75,000',
      date: '2026-02-10',
    },
  ];

  const demoPendingQuote = {
    id: 'QT-2026-045',
    requestTitle: 'Product Photography Session',
    items: [
      { description: 'Full-day product shoot', qty: 1, price: 25000 },
      { description: 'Photo editing (50 images)', qty: 50, price: 300 },
      { description: 'White background setup', qty: 1, price: 5000 },
    ],
    subtotal: 45000,
    gst: 8100,
    total: 53100,
    validUntil: '2026-03-01',
  };

  const demoCalendarItems = [
    { date: '2026-02-28', title: 'Brand Video - Final Review', status: 'scheduled', type: 'review' },
    { date: '2026-03-02', title: 'Product Photoshoot', status: 'confirmed', type: 'shoot' },
    { date: '2026-03-05', title: 'Social Media Launch', status: 'planned', type: 'delivery' },
    { date: '2026-03-10', title: 'Monthly Report Review', status: 'planned', type: 'meeting' },
  ];

  const availableServices = [
    { icon: Camera, name: 'Photography', desc: 'Professional photo shoots' },
    { icon: Video, name: 'Videography', desc: 'Corporate & promotional videos' },
    { icon: Globe, name: 'Digital Marketing', desc: 'Social media & ads' },
    { icon: Palette, name: 'Branding', desc: 'Logo & brand identity' },
    { icon: BookOpen, name: 'eLearning', desc: 'Video lectures & courses' },
  ];

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'requests', label: 'My Requests', icon: FileText },
    { id: 'quotes', label: 'Quotes', icon: DollarSign },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'uploads', label: 'Brand Kit', icon: Upload },
  ];

  const statusColors = {
    scheduled: 'bg-blue-500',
    confirmed: 'bg-green-500',
    planned: 'bg-yellow-500',
    completed: 'bg-gray-500',
  };

  return (
    <div className="min-h-screen py-32">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-primary/20 to-transparent border-b border-white/10 py-4 mb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-white font-medium">
                Experience the Focus Pixel Client Portal
              </span>
            </div>
            <Link
              to="/get-proposal"
              className="btn-primary text-sm py-2"
              data-testid="demo-cta-btn"
            >
              Start Your Project
              <ChevronRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <div>
            <p className="text-primary font-mono text-sm tracking-widest mb-2">CLIENT PORTAL DEMO</p>
            <h1 className="text-3xl md:text-4xl font-syne font-bold text-white">
              Welcome to Your Portal
            </h1>
            <p className="text-fp-textMuted mt-2">Manage requests, approve quotes, and track your projects</p>
          </div>
          <Link
            to="/login"
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="demo-login-btn"
          >
            Login to Your Account
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto gap-2 mb-8 pb-2 hide-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-sm transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary text-black font-medium'
                  : 'bg-fp-surface text-fp-textMuted hover:text-white'
              }`}
              data-testid={`demo-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: FileText, label: 'Total Requests', value: demoStats.totalRequests, color: 'text-primary' },
                  { icon: DollarSign, label: 'Pending Quotes', value: demoStats.pendingQuotes, color: 'text-yellow-400' },
                  { icon: TrendingUp, label: 'Active Projects', value: demoStats.activeProjects, color: 'text-green-400' },
                  { icon: Calendar, label: 'Upcoming', value: demoStats.upcomingBookings, color: 'text-blue-400' },
                ].map((stat) => (
                  <div key={stat.label} className="glass rounded-sm p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <stat.icon className={`w-4 h-4 ${stat.color}`} />
                      <span className="text-fp-textMuted text-sm">{stat.label}</span>
                    </div>
                    <p className="text-2xl font-syne font-bold text-white">{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="glass rounded-sm p-6">
                <h2 className="font-syne font-bold text-white text-xl mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-4 p-4 bg-fp-bg border border-yellow-500/20 rounded-sm">
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-white font-medium">1 Quote Awaiting Review</p>
                      <p className="text-fp-textMuted text-sm">Review and approve to proceed</p>
                    </div>
                    <button
                      onClick={() => setActiveTab('quotes')}
                      className="btn-ghost text-sm whitespace-nowrap"
                    >
                      Review
                    </button>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-fp-bg border border-primary/20 rounded-sm">
                    <Plus className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-white font-medium">Start a New Project</p>
                      <p className="text-fp-textMuted text-sm">Submit a service request</p>
                    </div>
                    <button
                      onClick={() => setActiveTab('requests')}
                      className="btn-ghost text-sm whitespace-nowrap"
                    >
                      New Request
                    </button>
                  </div>
                </div>
              </div>

              {/* Recent Requests */}
              <div className="glass rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-syne font-bold text-white text-xl">Recent Requests</h2>
                  <button onClick={() => setActiveTab('requests')} className="text-primary text-sm hover:underline">
                    View All
                  </button>
                </div>
                <div className="space-y-3">
                  {demoServiceRequests.slice(0, 2).map((request) => (
                    <div key={request.id} className="flex items-center gap-4 p-4 bg-fp-bg rounded-sm">
                      <div className={`w-2 h-8 rounded-full ${request.statusColor}`} />
                      <div className="flex-1">
                        <p className="text-white font-medium">{request.title}</p>
                        <p className="text-fp-textMuted text-sm">{request.service} • {request.id}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs rounded-full ${request.statusColor}/20 text-white`}>
                        {request.statusLabel}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-syne font-bold text-white text-xl">My Service Requests</h2>
                <button className="btn-primary text-sm" data-testid="new-request-demo-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Request
                </button>
              </div>

              {/* Service Categories */}
              <div className="glass rounded-sm p-6">
                <h3 className="text-white font-medium mb-4">Available Services</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {availableServices.map((service) => (
                    <div key={service.name} className="p-4 bg-fp-bg rounded-sm text-center hover:bg-white/5 transition-colors cursor-pointer">
                      <service.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                      <p className="text-white text-sm font-medium">{service.name}</p>
                      <p className="text-fp-textMuted text-xs">{service.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Request List */}
              <div className="space-y-4">
                {demoServiceRequests.map((request) => (
                  <div key={request.id} className="glass rounded-sm p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-3 h-3 rounded-full ${request.statusColor} mt-2`} />
                        <div>
                          <p className="text-fp-textMuted text-xs">{request.id}</p>
                          <h3 className="text-white font-semibold text-lg">{request.title}</h3>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded">
                              {request.service}
                            </span>
                            <span className="text-fp-textMuted text-sm">Budget: {request.budget}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 text-sm rounded-full ${request.statusColor}/20 text-white`}>
                          {request.statusLabel}
                        </span>
                        <button className="btn-ghost text-sm">View Details</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quotes Tab */}
          {activeTab === 'quotes' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-syne font-bold text-white text-xl">Quotes & Proposals</h2>
                <span className="text-fp-textMuted text-sm">1 pending review</span>
              </div>

              {/* Pending Quote */}
              <div className="glass rounded-sm overflow-hidden border-2 border-yellow-500/30">
                <div className="bg-yellow-500/10 px-6 py-3 border-b border-yellow-500/20">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-400" />
                    <span className="text-yellow-400 font-medium">Quote Pending Your Approval</span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-fp-textMuted text-sm">{demoPendingQuote.id}</p>
                      <h3 className="text-white font-semibold text-lg">{demoPendingQuote.requestTitle}</h3>
                    </div>
                    <p className="text-fp-textMuted text-sm">Valid until: {demoPendingQuote.validUntil}</p>
                  </div>

                  {/* Line Items */}
                  <div className="space-y-3 mb-6">
                    {demoPendingQuote.items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-fp-bg rounded">
                        <span className="text-white">{item.description}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-fp-textMuted">× {item.qty}</span>
                          <span className="text-white font-medium">₹{(item.qty * item.price).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="border-t border-white/10 pt-4 space-y-2">
                    <div className="flex justify-between text-fp-textMuted">
                      <span>Subtotal</span>
                      <span>₹{demoPendingQuote.subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-fp-textMuted">
                      <span>GST (18%)</span>
                      <span>₹{demoPendingQuote.gst.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold text-white pt-2">
                      <span>Total</span>
                      <span>₹{demoPendingQuote.total.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mt-6">
                    <button className="btn-primary flex-1" data-testid="approve-quote-demo">
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve Quote
                    </button>
                    <button className="btn-secondary flex-1" data-testid="revision-quote-demo">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Request Revision
                    </button>
                    <button className="btn-ghost px-4" data-testid="reject-quote-demo">
                      <XCircle className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Approved Quote */}
              <div className="glass rounded-sm p-6 border border-green-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-white font-medium">Corporate Brand Video</p>
                      <p className="text-fp-textMuted text-sm">QT-2026-043 • Approved on Feb 10, 2026</p>
                    </div>
                  </div>
                  <p className="text-green-400 font-bold">₹1,77,000</p>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === 'calendar' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-syne font-bold text-white text-xl">Project Calendar</h2>
                <span className="text-fp-textMuted text-sm">February - March 2026</span>
              </div>

              <div className="glass rounded-sm p-6">
                <div className="space-y-4">
                  {demoCalendarItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-4 bg-fp-bg border border-white/5 rounded-sm"
                    >
                      <div className="text-center min-w-[60px]">
                        <p className="text-white font-bold">{item.date.split('-')[2]}</p>
                        <p className="text-fp-textMuted text-xs">
                          {new Date(item.date).toLocaleDateString('en-US', { month: 'short' })}
                        </p>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${statusColors[item.status]}`} />
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.title}</p>
                        <p className="text-fp-textMuted text-sm capitalize">{item.type}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-sm capitalize ${
                        item.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                        item.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Brand Kit Tab */}
          {activeTab === 'uploads' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-syne font-bold text-white text-xl">Brand Kit & Assets</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="glass rounded-sm p-6">
                  <h3 className="font-semibold text-white mb-4">Your Brand Assets</h3>
                  <div className="space-y-3">
                    {['Logo Files', 'Brand Colors', 'Typography', 'Brand Guidelines', 'Product Images'].map((item) => (
                      <div key={item} className="flex items-center justify-between p-3 bg-fp-bg rounded-sm">
                        <span className="text-fp-textMuted">{item}</span>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="glass rounded-sm p-6">
                  <h3 className="font-semibold text-white mb-4">Upload New Assets</h3>
                  <div className="border-2 border-dashed border-white/10 rounded-sm p-8 text-center hover:border-primary/50 transition-colors cursor-pointer">
                    <Upload className="w-8 h-8 text-fp-textMuted mx-auto mb-4" />
                    <p className="text-fp-textMuted mb-2">Drag & drop files here</p>
                    <p className="text-fp-textMuted text-sm">or click to browse</p>
                    <p className="text-fp-textMuted text-xs mt-4">Supported: JPG, PNG, PDF, AI, PSD</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* CTA Banner */}
        <div className="mt-16 glass rounded-sm p-8 text-center">
          <h2 className="text-2xl font-syne font-bold text-white mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-fp-textMuted mb-6 max-w-xl mx-auto">
            Submit a service request and get a detailed quote within 24 hours. 
            Our team will guide you through every step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/get-proposal"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="demo-final-cta-btn"
            >
              <Send className="w-4 h-4" />
              Get a Proposal
            </Link>
            <Link
              to="/book-call"
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="demo-book-call-btn"
            >
              <Calendar className="w-4 h-4" />
              Book a Strategy Call
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPortalPage;
